import { useMemo } from "react";
import type { Channel, Program } from "@hybrid-iptv/types";
import { detectWebCapabilities, fileNameFor, openExternalSink, pickContainer } from "@hybrid-iptv/recorder";
import { useRecordRequest } from "../player/recordRequest.js";
import { dvrAvailable } from "./dvr.js";

/** Minutes a guide "Record now" keeps going past the programme's listed end. */
const OVERRUN_MS = 2 * 60_000;

export const APP_ONLY_TITLE = "Scheduled recording is in the SliqueTV Android app";

export type RecordActionKind = "dvr" | "now" | "app";

export interface RecordAction {
  /**
   * "dvr": the Android app's recorder dialog (any programme).
   * "now": the website records a programme airing now, into a file the viewer picks.
   * "app": the website cannot record this one (it has not started, or this browser
   *        cannot record) — shown greyed out, saying where it works.
   */
  kind: RecordActionKind;
  label: string;
  title: string;
}

/** Which Record the guide offers for a programme, here. Pure, for tests. */
export function recordActionFor(program: Pick<Program, "start" | "end">, nowMs: number, env: { dvr: boolean; webCanRecord: boolean; webPicker: boolean }): RecordAction {
  if (env.dvr) return { kind: "dvr", label: "Record", title: "Record this programme or the series" };
  const airing = new Date(program.start).getTime() <= nowMs && nowMs < new Date(program.end).getTime();
  if (airing && env.webCanRecord) {
    return {
      kind: "now",
      label: "Record now",
      title: env.webPicker
        ? "Record now until it ends — choose where to save (an external drive works)"
        : "Record now until it ends, to this browser",
    };
  }
  return {
    kind: "app",
    label: "Record",
    title: airing ? "This browser can't record. " + APP_ONLY_TITLE : APP_ONLY_TITLE,
  };
}

export function useRecordAction(program: Program, channel: Channel): RecordAction {
  return useMemo(() => {
    const caps = detectWebCapabilities();
    return recordActionFor(program, Date.now(), { dvr: dvrAvailable(), webCanRecord: caps.canRecord, webPicker: caps.canWriteExternalStorage });
    // The programme and channel identify the action; the clock is read when the menu opens.
  }, [program, channel]);
}

/**
 * The website's "Record now": ask where to save while the click still counts as
 * a user gesture (browsers refuse the picker later), then tune the channel and
 * leave the request for the player. Resolves false when the viewer cancels the picker.
 */
export async function startWebRecording(program: Program, channel: Channel, tune: () => void): Promise<boolean> {
  const caps = detectWebCapabilities();
  let sink;
  if (caps.canWriteExternalStorage) {
    const name = fileNameFor({ contentRef: channel.id, title: program.title, channelName: channel.name }, pickContainer() ?? "video/webm", new Date().toISOString());
    try {
      sink = await openExternalSink(name);
    } catch (error) {
      // Cancel in the save dialog: do nothing. Any other refusal (an embedded
      // browser that blocks the dialog): record to the browser's own storage
      // instead of silently doing nothing.
      if (error instanceof DOMException && error.name === "AbortError") return false;
      sink = undefined;
    }
  }
  useRecordRequest.getState().request({
    channelId: channel.id,
    title: program.title,
    ...(sink ? { sink } : {}),
    stopAtMs: new Date(program.end).getTime() + OVERRUN_MS,
  });
  tune();
  return true;
}
